import React, { useState } from "react";
import { Layout, Menu } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    ApiFilled,
    CompassFilled,
    FundFilled,
    LeftCircleFilled,
    MessageFilled,
    ProfileFilled,
    SafetyCertificateFilled,
} from "@ant-design/icons";
import { useUserState } from "../../../../common/UserContext";
import { TailSpendHeader } from "./TailSpendHeader";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { TailSpendProvider, useTailSpend } from "../TailSpendContext";
import { TourProvider, useTour } from "../tour/TourContext";
import { getNeedsAttentionDeals } from "../model/api";
import { Tour } from "../tour/Tour";
import classes from "./TailSpendLayout.module.less";

const { Content, Sider } = Layout;

const ICON_STYLE: React.CSSProperties = { color: "#1890ff" };

const SECTIONS: Array<{ key: string; path: string; label: string; icon: React.ReactNode }> = [
    { key: "negotiations", path: "/superadmin/demo/tail-spend/negotiations", label: "Negotiations", icon: <MessageFilled style={ICON_STYLE} /> },
    { key: "reporting", path: "/superadmin/demo/tail-spend/reporting", label: "Reporting", icon: <FundFilled style={ICON_STYLE} /> },
    { key: "suppliers", path: "/superadmin/demo/tail-spend/suppliers", label: "Suppliers", icon: <ProfileFilled style={ICON_STYLE} /> },
    { key: "guardrails", path: "/superadmin/demo/tail-spend/guardrails", label: "Guardrails", icon: <SafetyCertificateFilled style={ICON_STYLE} /> },
    { key: "setup", path: "/superadmin/demo/tail-spend/setup", label: "Setup", icon: <ApiFilled style={ICON_STYLE} /> },
];

const LayoutInner: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { me } = useUserState();
    const { workspace } = useTailSpend();
    const tour = useTour();
    const [collapsed, setCollapsed] = useState(true);

    const needsCount = getNeedsAttentionDeals(workspace).length;
    const selected = SECTIONS.find((s) => location.pathname.startsWith(s.path))?.key ?? "negotiations";

    return (
        <Layout className={classes.container}>
            <TailSpendHeader me={me} />
            <Layout>
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={() => setCollapsed((c) => !c)}
                    theme="light"
                    className={classes.sider}
                    collapsedWidth={55}
                    trigger={<SvgIcon name={collapsed ? "right" : "left"} color="#1890FF" />}
                >
                    <div className={classes.menuContainer}>
                        <Menu
                            theme="light"
                            mode="inline"
                            className={classes.menu}
                            selectedKeys={[selected]}
                        >
                            {SECTIONS.map((s) => (
                                <Menu.Item
                                    key={s.key}
                                    icon={s.icon}
                                    onClick={() => navigate(s.path)}
                                >
                                    {s.label}
                                    {s.key === "negotiations" && needsCount > 0 && (
                                        <span className={classes.badge}>{needsCount}</span>
                                    )}
                                </Menu.Item>
                            ))}
                            <div className={classes.divider}></div>
                            <Menu.Item
                                key="tour"
                                icon={<CompassFilled style={ICON_STYLE} />}
                                onClick={() => tour.start()}
                            >
                                Take a tour
                            </Menu.Item>
                            <Menu.Item
                                key="exit"
                                icon={<LeftCircleFilled style={ICON_STYLE} />}
                                onClick={() => navigate("/")}
                            >
                                Exit demo
                            </Menu.Item>
                        </Menu>
                    </div>
                </Sider>
                <Content className={classes.stage}>
                    <div className={classes.content}>
                        <Outlet />
                    </div>
                </Content>
            </Layout>
            <Tour />
        </Layout>
    );
};

const TailSpendLayout: React.FC = () => (
    <TailSpendProvider>
        <TourProvider>
            <LayoutInner />
        </TourProvider>
    </TailSpendProvider>
);

export default TailSpendLayout;
